const VARIANT_PRICE_SYNC_VERSION = 1

class ProductSubscriptions extends HTMLElement {
  constructor() {
    super()

    this.input = this.querySelector('input[name="selling_plan"]')
    this.options = this.querySelectorAll('input[name="purchase_option"]')
    this.select = this.querySelector('select[name="purchase_option_values"]')
    this.details = this.querySelector('[data-subscription-details]')
    this.cards = this.querySelectorAll('.sp-purchase-option-card')
    this.subscriptionPrice = this.querySelector('[data-subscription-price]')
    this.oneTimePrice = this.querySelector('[data-one-time-price]')
    this.comparePrice = this.querySelector('[data-compare-price]')
    this.form = this.closest('form')
    this.variantData = this.readVariantData()
    this.currentVariantId = ''
    this.variantSyncTimer = null
    this.variantPoller = null

    this.onProductOptionChange = this.queueVariantSync.bind(this)
    this.onThemeVariantChange = this.queueVariantSync.bind(this)

    this.initialise()
  }

  initialise() {
    this.options.forEach((option) =>
      option.addEventListener('change', this.onRadioChange.bind(this))
    )

    if (this.select) {
      this.select.addEventListener('change', this.onSelectChange.bind(this))
    }

    const checkedOption = Array.from(this.options).find((option) => option.checked)
    this.setSellingPlan(checkedOption?.value || '')
    this.updateSelectedCard()

    if (this.form) {
      this.form.addEventListener('change', this.onProductOptionChange, true)
      this.form.addEventListener('input', this.onProductOptionChange, true)
    }

    document.addEventListener('variant:change', this.onThemeVariantChange)
    document.addEventListener('variantChange', this.onThemeVariantChange)

    this.syncVariant(true)
    this.variantPoller = window.setInterval(() => this.syncVariant(), 250)
  }

  disconnectedCallback() {
    if (this.form) {
      this.form.removeEventListener('change', this.onProductOptionChange, true)
      this.form.removeEventListener('input', this.onProductOptionChange, true)
    }

    document.removeEventListener('variant:change', this.onThemeVariantChange)
    document.removeEventListener('variantChange', this.onThemeVariantChange)
    window.clearTimeout(this.variantSyncTimer)
    window.clearInterval(this.variantPoller)
  }

  readVariantData() {
    const variants = new Map()

    this.querySelectorAll('[data-subscription-variant]').forEach((variantNode) => {
      const allocations = Array.from(
        variantNode.querySelectorAll('[data-subscription-allocation]'),
      ).map((allocationNode) => ({
        planId: String(allocationNode.dataset.planId || ''),
        name: allocationNode.dataset.planName || '',
        price: allocationNode.dataset.price || '',
      })).filter((allocation) => allocation.planId)

      variants.set(String(variantNode.dataset.variantId || ''), {
        price: variantNode.dataset.price || '',
        allocations,
      })
    })

    return variants
  }

  getVariantInput() {
    const form = this.closest('form') || this.form
    return form?.querySelector('input[name="id"], select[name="id"]') || null
  }

  queueVariantSync() {
    window.clearTimeout(this.variantSyncTimer)
    this.variantSyncTimer = window.setTimeout(() => this.syncVariant(), 50)
  }

  syncVariant(force = false) {
    const variantInput = this.getVariantInput()
    const variantId = String(variantInput?.value || '')

    if (!variantId || (!force && variantId === this.currentVariantId)) return

    const variant = this.variantData.get(variantId)
    if (!variant) return

    this.currentVariantId = variantId
    const allocations = variant.allocations
    this.hidden = allocations.length === 0

    if (this.oneTimePrice) this.oneTimePrice.textContent = variant.price
    if (this.comparePrice) this.comparePrice.textContent = variant.price

    if (allocations.length === 0) {
      this.setSellingPlan('')
      return
    }

    const previousPlanId = this.select?.value || ''
    const selectedAllocation =
      allocations.find((allocation) => allocation.planId === previousPlanId) ||
      allocations[0]

    if (this.select) {
      this.select.replaceChildren()
      allocations.forEach((allocation) => {
        const option = document.createElement('option')
        option.value = allocation.planId
        option.textContent = allocation.name
        option.dataset.price = allocation.price
        option.selected = allocation.planId === selectedAllocation.planId
        this.select.append(option)
      })
    }

    const subscriptionOption = Array.from(this.options).find(
      (option) => option.value !== '',
    )
    if (subscriptionOption) subscriptionOption.value = selectedAllocation.planId
    if (this.subscriptionPrice) {
      this.subscriptionPrice.textContent = selectedAllocation.price
    }

    if (subscriptionOption?.checked) {
      this.setSellingPlan(selectedAllocation.planId)
    } else {
      this.setSellingPlan('')
    }

    this.updateSelectedCard()
  }

  onRadioChange(event) {
    const value = event.target.value || ''

    if (value && this.select) {
      const selectedPlanId = this.select.value
      event.target.value = selectedPlanId
      this.setSellingPlan(selectedPlanId)
    } else {
      this.setSellingPlan('')
    }

    this.updateSelectedCard()
  }

  onSelectChange(event) {
    const subscriptionOption = Array.from(this.options).find(
      (option) => option.value !== '',
    )

    if (subscriptionOption) {
      subscriptionOption.value = event.target.value
      subscriptionOption.checked = true
    }

    const selected = event.target.options[event.target.selectedIndex]
    if (this.subscriptionPrice && selected?.dataset.price) {
      this.subscriptionPrice.textContent = selected.dataset.price
    }

    this.setSellingPlan(event.target.value)
    this.updateSelectedCard()
  }

  setSellingPlan(value) {
    const sellingPlanId = value || ''
    if (this.input) this.input.value = sellingPlanId
    if (this.details) this.details.hidden = !sellingPlanId
  }

  updateSelectedCard() {
    this.cards.forEach((card) => {
      const radio = card.querySelector('input[type="radio"]')
      card.classList.toggle('is-selected', Boolean(radio?.checked))
    })
  }

  getCurrentSellingPlanId() {
    return this.input?.value || ''
  }
}

if (!customElements.get('product-subscriptions')) {
  customElements.define('product-subscriptions', ProductSubscriptions)
}

window.ProductSubscriptions = ProductSubscriptions

window.getCurrentSellingPlanId = function () {
  const productSubscriptions = document.querySelector('product-subscriptions')
  return productSubscriptions ? productSubscriptions.getCurrentSellingPlanId() : ''
}
